import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { connectEmulators } from './emulator';
import { validateFirebaseConfig } from '../utils/firebaseValidation';

// Get Firebase config from environment variables
// These should be set in .env file
// Note: React requires REACT_APP_ prefix and server restart after .env changes
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Debug: Log config status (only in development)
if (process.env.NODE_ENV === 'development') {
  console.log('🔍 Firebase Config Check:');
  console.log('API Key:', firebaseConfig.apiKey ? `${firebaseConfig.apiKey.substring(0, 10)}...` : 'MISSING');
  console.log('Project ID:', firebaseConfig.projectId || 'MISSING');
  console.log('Auth Domain:', firebaseConfig.authDomain || 'MISSING');
  
  if (!firebaseConfig.apiKey) {
    console.error('❌ REACT_APP_FIREBASE_API_KEY is missing!');
    console.error('💡 Make sure:');
    console.error('   1. .env file exists in project root');
    console.error('   2. Variable name starts with REACT_APP_');
    console.error('   3. Dev server was restarted after .env changes');
  }
}

// Validate required config (only warn in development, error in production)
const isDevelopment = process.env.NODE_ENV === 'development';
const hasValidConfig = firebaseConfig.apiKey && 
                       firebaseConfig.projectId && 
                       !firebaseConfig.apiKey.includes('demo') &&
                       !firebaseConfig.projectId.includes('demo');

if (!hasValidConfig) {
  if (isDevelopment) {
    console.warn('⚠️ Firebase configuration missing or using demo values.');
    console.warn('📖 Please check your .env file and ensure all Firebase credentials are set.');
    console.warn('📋 See FIREBASE_API_KEYS_GUIDE.md for setup instructions.');
  } else {
    console.error('❌ Firebase configuration is invalid. Please check your .env file.');
    console.error('📖 See FIREBASE_API_KEYS_GUIDE.md for setup instructions.');
  }
}

// Validate Firebase configuration
if (process.env.NODE_ENV !== 'test') {
  const isValid = validateFirebaseConfig();
  if (!isValid) {
    const isDevelopment = process.env.NODE_ENV === 'development';
    if (isDevelopment) {
      console.warn('⚠️ Firebase configuration validation failed.');
      console.warn('📖 See FIREBASE_API_KEYS_GUIDE.md for setup instructions.');
    } else {
      console.error('❌ Firebase configuration is invalid. Please check your .env file.');
      console.error('📖 See FIREBASE_API_KEYS_GUIDE.md for setup instructions.');
    }
  }
}

// Validate that API key exists before initializing Firebase
if (!firebaseConfig.apiKey) {
  const errorMsg = 'Firebase API key is missing. Please check your .env file and restart the dev server.';
  console.error('❌', errorMsg);
  throw new Error(errorMsg);
}

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
// Explicit database id avoids Firestore Listen channel 400 Bad Request (WebChannel)
export const db = getFirestore(app, '(default)');

// Local dev: Firestore & Auth use Cloud; Functions use emulator. Prod: all Cloud.
connectEmulators(auth, db, app);

export default app;

